<template>
  <div class="container">
    <div class="form-sections">
      <!-- Loading state -->
      <div v-if="loading" class="empty-state">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Loading...</p>
      </div>

      <!-- Document Template Form -->
      <div v-if="!loading" class="step-content">
        <!-- Shared Details: Category / Module / Sub Module -->
        <div class="info-section">
          <h5 class="section-title">Template Details</h5>
          <div class="dropdown-container single-column">
            <!-- Document Category -->
            <div class="dropdown-item">
              <label for="documentCategory"
                >Document Category<span class="req">*</span></label
              >
              <select
                id="documentCategory"
                v-model="selectedCategory"
                class="scrollable-dropdown"
                :disabled="loadingCategories"
                required
              >
                <option disabled value="">
                  {{
                    loadingCategories
                      ? "Loading..."
                      : "Select Document Category"
                  }}
                </option>
                <option
                  v-for="category in categoryList"
                  :key="category.id"
                  :value="category.id"
                >
                  {{ category.name }}
                </option>
              </select>
            </div>

            <!-- Module -->
            <div class="dropdown-item">
              <label for="module">Module<span class="req">*</span></label>
              <select
                id="module"
                v-model="selectedModule"
                class="scrollable-dropdown"
                :disabled="loadingModules"
                required
              >
                <option disabled value="">
                  {{ loadingModules ? "Loading..." : "Select Module" }}
                </option>
                <option
                  v-for="module in moduleList"
                  :key="module.id"
                  :value="module.id"
                >
                  {{ module.name }}
                </option>
              </select>
            </div>

            <!-- Sub Module -->
            <div class="dropdown-item">
              <label for="subModule">
                Sub Module<span class="req">*</span>
                <span v-if="loadingSubModules" class="loading-indicator">
                  <i class="fas fa-spinner fa-spin"></i>
                  Loading...
                </span>
              </label>
              <select
                id="subModule"
                v-model="selectedSubModule"
                class="scrollable-dropdown"
                :disabled="!selectedModule || loadingSubModules"
                required
              >
                <option disabled value="">
                  {{
                    loadingSubModules
                      ? "Loading..."
                      : !selectedModule
                      ? "Please select Module first"
                      : "Select Sub Module"
                  }}
                </option>
                <option
                  v-for="subModule in subModuleList"
                  :key="subModule.id"
                  :value="subModule.id"
                >
                  {{ subModule.name }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Templates List -->
        <div class="info-section">
          <div class="templates-section-header">
            <h5 class="section-title no-border">
              {{ isEditMode ? "Template" : "Templates" }}
            </h5>
            <button
              v-if="!isEditMode"
              type="button"
              class="btn add-template-btn"
              @click="addTemplateRow"
            >
              <i class="fas fa-plus"></i> Add Another Template
            </button>
          </div>

          <div
            v-for="(row, index) in templates"
            :key="row.key"
            class="template-row"
          >
            <div class="template-row-header">
              <span class="template-row-index">Template {{ index + 1 }}</span>
              <button
                v-if="!isEditMode && templates.length > 1"
                type="button"
                class="remove-row-btn"
                title="Remove this template"
                @click="removeTemplateRow(index)"
              >
                <i class="fas fa-trash"></i>
              </button>
            </div>

            <div class="template-row-body">
              <!-- Row fields -->
              <div class="template-row-fields">
                <div class="dropdown-item">
                  <label :for="`documentName-${index}`"
                    >Document Name<span class="req">*</span></label
                  >
                  <input
                    :id="`documentName-${index}`"
                    type="text"
                    placeholder="Enter Document Name"
                    v-model="row.documentName"
                    required
                  />
                </div>

                <div class="required-toggle-container">
                  <div class="required-toggle-label">
                    Is this document required?
                  </div>
                  <label class="toggle-switch">
                    <input
                      type="checkbox"
                      v-model="row.isRequired"
                      :true-value="1"
                      :false-value="0"
                    />
                    <span class="toggle-slider"></span>
                  </label>
                </div>

                <div class="required-toggle-container">
                  <div class="required-toggle-label">
                    Allow multiple file uploads?
                  </div>
                  <label class="toggle-switch">
                    <input
                      type="checkbox"
                      v-model="row.isMultiple"
                      :true-value="1"
                      :false-value="0"
                    />
                    <span class="toggle-slider"></span>
                  </label>
                </div>
              </div>

              <!-- Row upload -->
              <div class="template-row-upload">
                <div
                  class="document-image-container row-upload-box"
                  :class="{ 'drag-active': row.isDragging }"
                  @mouseenter="row.showActions = true"
                  @mouseleave="row.showActions = false"
                  @click="triggerFileInput(index)"
                  @dragover.prevent="row.isDragging = true"
                  @dragleave.prevent="row.isDragging = false"
                  @drop.prevent="handleDrop(index, $event)"
                >
                  <!-- Image preview -->
                  <img
                    v-if="row.filePreview && isImageDocument(row.fileType)"
                    :src="row.filePreview"
                    alt="Template File"
                    class="document-image"
                  />

                  <!-- PDF -->
                  <div
                    v-else-if="row.filePreview && isPdfDocument(row.fileType)"
                    class="pdf-preview"
                  >
                    <i class="fas fa-file-pdf"></i>
                    <span>{{ row.fileName || "Template Document" }}</span>
                    <small>PDF Document</small>
                  </div>

                  <!-- Word -->
                  <div
                    v-else-if="row.filePreview && isWordDocument(row.fileType)"
                    class="pdf-preview"
                  >
                    <i class="fas fa-file-word"></i>
                    <span>{{ row.fileName || "Template Document" }}</span>
                    <small>Word Document</small>
                  </div>

                  <!-- Excel -->
                  <div
                    v-else-if="row.filePreview && isExcelDocument(row.fileType)"
                    class="pdf-preview"
                  >
                    <i class="fas fa-file-excel"></i>
                    <span>{{ row.fileName || "Template Document" }}</span>
                    <small>Excel Document</small>
                  </div>

                  <!-- Invalid -->
                  <div v-else-if="row.filePreview" class="invalid-preview">
                    <i class="fas fa-exclamation-triangle"></i>
                    <span>Invalid Document</span>
                    <small>Unsupported format</small>
                  </div>

                  <!-- Placeholder -->
                  <div v-else class="document-placeholder">
                    <i
                      :class="
                        row.isDragging
                          ? 'fas fa-cloud-download-alt'
                          : 'fas fa-file-upload'
                      "
                    ></i>
                    <span>{{
                      row.isDragging
                        ? "Drop file to upload"
                        : "Click or Drag & Drop to Upload"
                    }}</span>
                    <small>Supported: PDF, JPG, PNG, JPEG, DOCX, XLSX</small>
                  </div>

                  <!-- Actions overlay -->
                  <div
                    class="document-actions-overlay"
                    v-if="
                      row.showActions &&
                      row.filePreview &&
                      isValidDocument(row.filePreview, row.fileType)
                    "
                  >
                    <div class="action-buttons">
                      <button
                        class="action-btn preview-btn"
                        @click.stop="
                          handleDocumentClick(
                            row.filePreview,
                            row.documentName || 'Template File',
                            row.fileType,
                          )
                        "
                        title="Preview"
                      >
                        <i class="fas fa-search-plus"></i>
                      </button>
                      <button
                        class="action-btn change-btn"
                        @click.stop="triggerFileInput(index)"
                        title="Change"
                      >
                        <i class="fas fa-sync"></i>
                      </button>
                      <button
                        class="action-btn remove-btn"
                        @click.stop="removeRowFile(index)"
                        title="Remove"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                    </div>
                    <div class="action-labels">
                      <span>Preview</span>
                      <span>Change</span>
                      <span>Remove</span>
                    </div>
                  </div>

                  <!-- Upload progress -->
                  <div v-if="row.uploading" class="upload-progress">
                    <div class="progress-bar">
                      <div
                        class="progress-fill"
                        :style="{ width: row.progress + '%' }"
                      ></div>
                    </div>
                    <span>{{ row.progress }}%</span>
                  </div>
                </div>

                <input
                  type="file"
                  ref="fileInputs"
                  @change="handleFileChange(index, $event)"
                  accept=".pdf,image/jpeg,image/jpg,image/png,.docx,.xlsx"
                  class="file-input-hidden"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Form Navigation -->
        <div class="step-navigation">
          <div class="step-navigation-left">
            <button type="button" class="btn btn-secondary" @click="cancel">
              Cancel
            </button>
          </div>
          <div class="step-navigation-right">
            <button
              type="button"
              class="btn btn-success"
              @click="submitTemplate"
              :disabled="saving"
            >
              <span
                v-if="saving"
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              <span v-if="!saving">{{ isEditMode ? "Update" : "Save" }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Document View Modal -->
    <DocumentViewModal
      :show="showImageModal"
      :documentSrc="selectedImage"
      :documentTitle="selectedImageTitle"
      :documentType="selectedDocumentType"
      @close="closeImageModal"
    />
  </div>
</template>

<script>
import {
  fetchUserRoleListForUserForm,
  fetchDistributorUserRoleListForUserForm,
  getModules,
  getSubModulesList,
  createDocumentTemplate,
  viewDocumentTemplateDetails,
  updateDocumentTemplate,
} from "@/services/dataService";
import { useToast } from "vue-toastification";
import DocumentViewModal from "@/pages/Modal/documents/documentViewModal.vue";

export default {
  components: { DocumentViewModal },
  data() {
    return {
      token: localStorage.getItem("token_set") || "",
      profileId: localStorage.getItem("profile_id") || "",
      userCategory: localStorage.getItem("user_label") || "",

      loading: false,
      saving: false,

      // Lists
      categoryList: [],
      moduleList: [],
      subModuleList: [],
      loadingCategories: false,
      loadingModules: false,
      loadingSubModules: false,

      // Shared fields
      selectedCategory: "",
      selectedModule: "",
      selectedSubModule: "",

      // Repeatable template rows
      templates: [this.makeTemplateRow()],

      // Shared preview modal
      showImageModal: false,
      selectedImage: "",
      selectedImageTitle: "",
      selectedDocumentType: null,

      templateId: this.$route.params.id || null,
    };
  },

  computed: {
    isEditMode() {
      return !!this.templateId;
    },
  },

  created() {
    this.loadCategoryList();
    this.loadModuleList();
    if (this.isEditMode) {
      this.loadExistingTemplate();
    }
  },

  setup() {
    const toast = useToast();
    return { toast };
  },

  watch: {
    selectedModule(newVal) {
      this.selectedSubModule = "";
      if (newVal) {
        this.loadSubModuleList();
      } else {
        this.subModuleList = [];
      }
    },
  },

  methods: {
    makeTemplateRow(existing = null) {
      return {
        key: existing ? `existing-${existing.id}` : `new-${Date.now()}-${Math.random()}`,
        id: existing ? existing.id : null,
        documentName: existing ? existing.document_name : "",
        isRequired: existing ? existing.is_required : 1,
        isMultiple: existing ? existing.is_multiple : 0,
        file: null,
        filePreview: existing ? existing.template_file_url : null,
        fileType: existing ? this.guessMimeFromExtension(existing.template_file_name) : null,
        fileName: existing ? existing.template_file_name : "",
        isDragging: false,
        showActions: false,
        uploading: false,
        progress: 0,
      };
    },

    addTemplateRow() {
      this.templates.push(this.makeTemplateRow());
    },

    removeTemplateRow(index) {
      this.templates.splice(index, 1);
    },

    async loadExistingTemplate() {
      try {
        this.loading = true;
        const response = await viewDocumentTemplateDetails(this.templateId, this.token);
        const data = response.data?.data;
        if (!data) return;

        this.selectedCategory = data.document_category_id;
        this.selectedModule = data.module_id;
        this.templates = [this.makeTemplateRow(data)];

        await this.loadSubModuleList();
        this.selectedSubModule = data.sub_module_id;
      } catch (error) {
        console.error("Error loading document template:", error);
        this.toast.error("Failed to load document template.");
      } finally {
        this.loading = false;
      }
    },

    async loadCategoryList() {
      try {
        this.loadingCategories = true;

        const response =
          this.userCategory === "DISTRIBUTOR"
            ? await fetchDistributorUserRoleListForUserForm(this.token)
            : await fetchUserRoleListForUserForm(this.token);

        this.categoryList = response.data.data || [];
      } catch (error) {
        console.error("Error fetching document categories:", error);
        this.toast.error("Failed to load document categories.");
      } finally {
        this.loadingCategories = false;
      }
    },

    async loadModuleList() {
      try {
        this.loadingModules = true;
        const response = await getModules(this.token);
        this.moduleList = response.data.data || [];
      } catch (error) {
        console.error("Error fetching modules:", error);
        this.toast.error("Failed to load modules.");
      } finally {
        this.loadingModules = false;
      }
    },

    async loadSubModuleList() {
      if (!this.selectedModule) {
        this.subModuleList = [];
        return;
      }

      try {
        this.loadingSubModules = true;
        const response = await getSubModulesList(this.token, this.selectedModule);
        this.subModuleList = response.data.data || [];
      } catch (error) {
        console.error("Error fetching sub modules:", error);
        this.toast.error("Failed to load sub modules.");
        this.subModuleList = [];
      } finally {
        this.loadingSubModules = false;
      }
    },

    triggerFileInput(index) {
      const input = this.$refs.fileInputs[index];
      if (input) input.click();
    },

    handleFileChange(index, event) {
      const file = event.target.files[0];
      if (file) {
        this.processRowFile(index, file);
      }
    },

    handleDrop(index, event) {
      this.templates[index].isDragging = false;
      const file = event.dataTransfer.files && event.dataTransfer.files[0];
      if (file) {
        this.processRowFile(index, file);
      }
    },

    async processRowFile(index, file) {
      if (!this.validateFile(file)) return;

      const row = this.templates[index];
      row.file = file;
      row.fileName = file.name;
      row.fileType = file.type;
      row.uploading = true;
      row.progress = 0;

      await this.simulateUploadProgress(index);

      const reader = new FileReader();
      reader.onload = (e) => {
        row.filePreview = e.target.result;
        row.uploading = false;
      };
      reader.readAsDataURL(file);
    },

    simulateUploadProgress(index) {
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          this.templates[index].progress += 10;
          if (this.templates[index].progress >= 100) {
            clearInterval(interval);
            resolve();
          }
        }, 100);
      });
    },

    removeRowFile(index) {
      const row = this.templates[index];
      row.file = null;
      row.filePreview = null;
      row.fileType = null;
      row.fileName = "";
      const input = this.$refs.fileInputs[index];
      if (input) input.value = "";
    },

    isImageDocument(documentType) {
      return documentType && documentType.startsWith("image/");
    },
    isPdfDocument(documentType) {
      return documentType === "application/pdf";
    },
    isWordDocument(documentType) {
      return documentType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    },
    isExcelDocument(documentType) {
      return documentType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    },
    isValidDocument(documentUrl, documentType) {
      if (!documentUrl || !documentType) return false;
      return (
        this.isImageDocument(documentType) ||
        this.isPdfDocument(documentType) ||
        this.isWordDocument(documentType) ||
        this.isExcelDocument(documentType)
      );
    },

    handleDocumentClick(documentUrl, title, documentType) {
      this.openDocumentModal(documentUrl, title, documentType);
    },

    openDocumentModal(documentUrl, title = "", documentType = null) {
      this.selectedImage = documentUrl;
      this.selectedImageTitle = title;
      this.selectedDocumentType = documentType;
      this.showImageModal = true;
    },

    closeImageModal() {
      this.showImageModal = false;
      this.selectedImage = "";
      this.selectedImageTitle = "";
      this.selectedDocumentType = null;
    },

    validateFile(file) {
      const maxSize = 5 * 1024 * 1024;

      if (file.size > maxSize) {
        this.toast.error("File size must be less than 5MB");
        return false;
      }

      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];
      if (!allowedTypes.includes(file.type)) {
        this.toast.error("Please select a PDF, JPG, PNG, JPEG, DOCX, or XLSX file");
        return false;
      }

      return true;
    },

    validateFormWithToast() {
      if (!this.selectedCategory) {
        this.toast.error("Document Category is required.");
        return false;
      }
      if (!this.selectedModule) {
        this.toast.error("Module is required.");
        return false;
      }
      if (!this.selectedSubModule) {
        this.toast.error("Sub Module is required.");
        return false;
      }

      for (let i = 0; i < this.templates.length; i++) {
        const row = this.templates[i];
        if (!row.documentName.trim()) {
          this.toast.error(`Template ${i + 1}: Document Name is required.`);
          return false;
        }
        if (!row.file && !row.filePreview) {
          this.toast.error(`Template ${i + 1}: Template file is required.`);
          return false;
        }
      }

      return true;
    },

    async submitTemplate() {
      if (!this.validateFormWithToast()) {
        return;
      }
      if (this.isEditMode) {
        await this.updateTemplate();
      } else {
        await this.createTemplates();
      }
    },

    toBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = (error) => reject(error);
      });
    },

    // Create mode
    async createTemplates() {
      try {
        this.saving = true;

        const templatesPayload = [];
        for (const row of this.templates) {
          templatesPayload.push({
            document_name: row.documentName,
            is_required: row.isRequired,
            is_multiple: row.isMultiple,
            template_file: row.file ? await this.toBase64(row.file) : "",
            template_file_name: row.fileName,
          });
        }

        const payload = {
          document_category_id: Number(this.selectedCategory),
          module_id: Number(this.selectedModule),
          sub_module_id: Number(this.selectedSubModule),
          created_by: Number(this.profileId),
          templates: templatesPayload,
        };

        const response = await createDocumentTemplate(payload, this.token);

        if (response.data.status === "success") {
          this.toast.success(response.data.message);
          this.$router.push("/document-templates-management");
        } else {
          let errorMessage = response.data.message || "Validation Error!";

          if (response.data.data && Object.keys(response.data.data).length > 0) {
            errorMessage += "\n\n";
            Object.keys(response.data.data).forEach((field) => {
              const errors = response.data.data[field];
              (Array.isArray(errors) ? errors : [errors]).forEach((error) => {
                errorMessage += "• " + error + "\n";
              });
            });
          }

          this.toast.error(errorMessage);
        }
      } catch (error) {
        console.error("Save document templates error:", error);

        if (error.response && error.response.data) {
          let errorMessage = error.response.data.message || "Validation Error!";

          if (
            error.response.data.data &&
            Object.keys(error.response.data.data).length > 0
          ) {
            errorMessage += "\n\n";
            Object.keys(error.response.data.data).forEach((field) => {
              const errors = error.response.data.data[field];
              (Array.isArray(errors) ? errors : [errors]).forEach((err) => {
                errorMessage += "• " + err + "\n";
              });
            });
          }

          this.toast.error(errorMessage);
        } else {
          this.toast.error("Failed to create document templates. Please try again.");
        }
      } finally {
        this.saving = false;
      }
    },

    // Edit mode
    async updateTemplate() {
      try {
        this.saving = true;
        const row = this.templates[0];

        const payload = {
          document_category_id: Number(this.selectedCategory),
          module_id: Number(this.selectedModule),
          sub_module_id: Number(this.selectedSubModule),
          document_name: row.documentName,
          is_required: row.isRequired,
          is_multiple: row.isMultiple,
        };

        if (row.file) {
          payload.template_file = await this.toBase64(row.file);
          payload.template_file_name = row.fileName;
        }

        const response = await updateDocumentTemplate(this.templateId, this.token, payload);

        if (response.data.status === "success") {
          this.toast.success(response.data.message);
          this.$router.push("/document-templates-management");
        } else {
          this.toast.error(response.data.message || "Validation Error!");
        }
      } catch (error) {
        console.error("Update document template error:", error);
        this.toast.error("Failed to update document template. Please try again.");
      } finally {
        this.saving = false;
      }
    },

    cancel() {
      this.$router.push("/document-templates-management");
    },

    guessMimeFromExtension(filename) {
      const ext = (filename || "").split(".").pop().toLowerCase();
      const map = {
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        png: "image/png",
        pdf: "application/pdf",
        docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      };
      return map[ext] || "";
    },
  },
};
</script>

<style scoped>
.req {
  color: red;
  margin-left: 5px;
}

.form-sections {
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 100%;
}

.section-title {
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #ccc;
  color: #333;
}

.section-title.no-border {
  margin: 0;
  padding: 0;
  border-bottom: none;
}

.scrollable-dropdown {
  max-height: 50px;
  overflow-y: auto;
}

.scrollable-dropdown:disabled {
  background-color: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
  opacity: 0.8;
}

.loading-indicator {
  margin-left: 10px;
  font-size: 0.85rem;
  color: #1c66ad;
  font-weight: normal;
}

.loading-indicator i {
  margin-right: 5px;
}

.container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 20px;
  margin: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  box-sizing: border-box;
}

.dropdown-container.single-column {
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
}

.dropdown-item {
  width: 100%;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 14px;
}

select,
input,
textarea {
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
}

select:focus,
input:focus,
textarea:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

select:disabled {
  background-color: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
  opacity: 0.7;
}

.file-input-hidden {
  display: none;
}

/* Document Required Toggle */
.required-toggle-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 5px;
  padding: 15px;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
}

.required-toggle-label {
  font-weight: bold;
  color: #333;
  font-size: 14px;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 52px;
  height: 30px;
  flex-shrink: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 34px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 22px;
  width: 22px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

.toggle-switch input:checked + .toggle-slider {
  background-color: #28a745;
}

.toggle-switch input:checked + .toggle-slider:before {
  transform: translateX(22px);
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
}

.info-section {
  background: white;
  border-radius: 10px;
  padding: 1.5rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  margin-bottom: 1rem;
  width: 100%;
}

/* Templates list */
.templates-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  padding-bottom: 10px;
  border-bottom: 2px solid #ccc;
}

.add-template-btn {
  background-color: #eaf2ff;
  color: #1c66ad;
  border: 1px solid #cfe1fb;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: unset;
}

.add-template-btn:hover {
  background-color: #1c66ad;
  color: #fff;
  border-color: #1c66ad;
}

.template-row {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 1.25rem;
  margin-bottom: 1.25rem;
  background-color: #fafbfc;
}

.template-row:last-child {
  margin-bottom: 0;
}

.template-row-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.template-row-index {
  font-weight: 700;
  color: #1c66ad;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.remove-row-btn {
  border: none;
  background: transparent;
  color: #dc2626;
  cursor: pointer;
  font-size: 1rem;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.remove-row-btn:hover {
  background-color: #fef2f2;
}

.template-row-body {
  display: flex;
  gap: 20px;
}

.template-row-fields {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 15px;
  min-width: 0;
}

.template-row-upload {
  width: 260px;
  flex-shrink: 0;
}

.row-upload-box {
  height: 180px;
}

.document-image-container {
  position: relative;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.document-image-container:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.document-image-container.drag-active {
  box-shadow: 0 0 0 3px #1c66ad, 0 4px 12px rgba(28, 102, 173, 0.3);
  transform: scale(1.02);
}

.document-image-container.drag-active .document-placeholder {
  background-color: #e0f2fe;
  border-color: #1c66ad;
  color: #1c66ad;
}

.document-image-container.drag-active .document-placeholder i {
  color: #1c66ad;
}

.document-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.document-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f3f4f6;
  color: #6b7280;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 0.75rem;
  text-align: center;
  transition: all 0.3s ease;
}

.document-placeholder i {
  font-size: 1.6rem;
  margin-bottom: 0.4rem;
  color: #9ca3af;
}

.document-placeholder span {
  font-size: 0.8rem;
  font-weight: 500;
}

.document-placeholder small {
  display: block;
  margin-top: 4px;
  font-size: 0.7rem;
  color: #9ca3af;
}

.document-image-container:hover .document-placeholder {
  background-color: #e5e7eb;
  border-color: #9ca3af;
}

.pdf-preview {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #fef3f2;
  color: #dc2626;
  border-radius: 8px;
  padding: 0.75rem;
  text-align: center;
}

.pdf-preview i {
  font-size: 2.2rem;
  margin-bottom: 0.4rem;
}

.pdf-preview span {
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.2rem;
  font-size: 0.8rem;
  word-break: break-word;
}

.pdf-preview small {
  display: block;
  margin-top: 4px;
  font-size: 0.7rem;
  color: #6b7280;
}

.invalid-preview {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #fef3f2;
  color: #dc2626;
  border-radius: 8px;
  padding: 0.75rem;
  text-align: center;
}

.invalid-preview i {
  font-size: 2.2rem;
  margin-bottom: 0.4rem;
}

.invalid-preview span {
  font-size: 0.8rem;
  font-weight: 500;
}

.invalid-preview small {
  display: block;
  margin-top: 4px;
  font-size: 0.7rem;
  color: #dc2626;
}

.document-actions-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 1;
  transition: opacity 0.3s ease;
}

.action-btn {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.preview-btn {
  background: linear-gradient(45deg, #2196f3, #21cbf3);
}

.preview-btn:hover {
  background: linear-gradient(45deg, #1976d2, #03a9f4);
  transform: scale(1.15);
  box-shadow: 0 6px 12px rgba(33, 150, 243, 0.4);
}

.change-btn {
  background: linear-gradient(45deg, #4caf50, #66bb6a);
}

.change-btn:hover {
  background: linear-gradient(45deg, #388e3c, #4caf50);
  transform: scale(1.15);
  box-shadow: 0 6px 12px rgba(76, 175, 80, 0.4);
}

.remove-btn {
  background: linear-gradient(45deg, #f44336, #e53935);
}

.remove-btn:hover {
  background: linear-gradient(45deg, #d32f2f, #c62828);
  transform: scale(1.15);
  box-shadow: 0 6px 12px rgba(244, 67, 54, 0.4);
}

.action-buttons {
  display: flex;
  gap: 10px;
  margin-bottom: 8px;
}

.action-labels {
  display: flex;
  gap: 16px;
  color: white;
  font-size: 10px;
  font-weight: 600;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
}

.action-labels span {
  text-align: center;
  min-width: 38px;
}

.upload-progress {
  position: absolute;
  bottom: 8px;
  left: 8px;
  right: 8px;
  background: rgba(255, 255, 255, 0.9);
  padding: 6px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.progress-bar {
  flex: 1;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(45deg, #4caf50, #66bb6a);
  transition: width 0.3s ease;
}

.upload-progress span {
  font-size: 0.7rem;
  font-weight: 600;
  color: #374151;
  min-width: 26px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #6b7280;
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #d1d5db;
}

.step-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
  width: 100%;
}

.step-navigation-left {
  display: flex;
  justify-content: flex-start;
  flex: 1;
}

.step-navigation-right {
  display: flex;
  justify-content: flex-end;
  flex: 1;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.3s;
  min-width: 100px;
}

.btn-secondary {
  background-color: #6b7280;
  color: white;
}

.btn-secondary:hover {
  background-color: #4b5563;
  transform: scale(1.05);
}

.btn-success {
  background-color: #28a745;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background-color: #218838;
  transform: scale(1.05);
}

.btn-success:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
  transform: none;
}

@media (max-width: 768px) {
  .template-row-body {
    flex-direction: column;
  }

  .template-row-upload {
    width: 100%;
  }

  .templates-section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .add-template-btn {
    width: 100%;
    justify-content: center;
  }

  .step-navigation {
    flex-direction: column;
    gap: 10px;
  }

  .step-navigation-left,
  .step-navigation-right {
    justify-content: center;
    width: 100%;
  }

  .step-navigation .btn {
    width: 100%;
  }
}
</style>